import axios from "axios";
import React from "react";

import { useLocalStorage } from "@advice/hooks/localStorage";
import { useSessionStorage } from "@advice/hooks/sessionStorage";
import { JobAlertFormValues } from "@themuse/muse-front-end/dist/molecules/job_alert_modal/JobAlertForm";
import { JobAlerts, JobAlertsProps } from "@themuse/muse-front-end/dist/organisms/job_alerts/component";
import { getCookie } from "@util/cookie";
import { translateDMAString } from "@util/location";
import { isEmail } from "@util/strings";
import { pushDataLayerEvent, pushDataLayerVariable } from "@util/tracking";

declare global {
    interface Window {
        google_tag_manager: any;
        dataLayer: any;
        localStorage: any;
    }
}

const jobAlertSubscribeRequest = async (values: JobAlertFormValues) => {
    const emailSubscription = {
        email: values.email,
        subscription_type: "job_alert",
        location: values.location.value,
        keyword: values.category.value,
        optional_newsletter: values.newsletter
    };

    const config = {
        headers: { "X-Xsrftoken": getCookie("_xsrf") }
    };
    const jobAlertSuccess = await axios
        .post("/api/email_alerts/subscribe", emailSubscription, config)
        .then(response => {
            const { data } = response;
            if (response.status === 200 || response.status === 201) {
                // Push subscription_id to dataLayer for tracking
                if (data.subscription_id) {
                    pushDataLayerVariable({ emailSubscriptionID: data.subscription_id });
                }
                return true;
            }
            throw new Error(`Job alert subscription was unsuccessful. Status: ${response.status}`);
        })
        .catch(error => {
            console.error(error);
            return false;
        });

    return jobAlertSuccess;
};

const JobAlert = () => {
    const [showJobAlert, setShowJobAlert] = React.useState(false);
    const [userHasSubscribed, setUserHasSubscribed] = useLocalStorage("job_alert_subscription");
    const [lastClosedForm, setLastClosedForm] = useLocalStorage("job_alert_form_closed");
    const [userCountry] = useSessionStorage("carmen_country");
    const [userDMA] = useSessionStorage("carmen_dma");
    const [userCity] = useSessionStorage("carmen_city");
    const [showFormModal, setShowFormModal] = React.useState(false);

    const oneDayMilliseconds = 86400000;
    const jobAlertHashExists = window.location.hash === "#job-alert";
    const formNotClosedInLast24Hours = Date.now() - (Date.parse(lastClosedForm) || 0) > oneDayMilliseconds;

    const shouldJobAlertShow = () => {
        // Check if the user has already subscribed to a job alert
        if (!jobAlertHashExists && userHasSubscribed) {
            setShowJobAlert(false);
            return;
        }
        setShowJobAlert(true);
    };

    React.useEffect(() => {
        shouldJobAlertShow();
        pushDataLayerVariable({ jobAlertCampaignType: "jobalerts_overlay_Sep2020" });
        if (formNotClosedInLast24Hours || jobAlertHashExists) {
            let delay = 5000;
            if (jobAlertHashExists) {
                delay = 500;
            }
            setTimeout(() => {
                setShowFormModal(true);
            }, delay);
        }
    }, []);

    const handleModalFormSubmit = async (values: JobAlertFormValues) => {
        const { category, email, location } = values;
        const jobAlertSuccess = await jobAlertSubscribeRequest(values);
        const validationError = !category.value || !location.value || !isEmail(email) ? "validation" : null;
        const errorType = !validationError && !jobAlertSuccess ? "Request Failed" : null;
        jobAlertSubscribeRequest(values);
        if (!jobAlertSuccess) {
            window.google_tag_manager["GTM-P3K3QB"].dataLayer.set("jobAlertErrorType", errorType);
        }
        if (jobAlertSuccess) {
            window.google_tag_manager["GTM-P3K3QB"].dataLayer.set("jobAlertErrorType", null);
            setUserHasSubscribed("true");
        }
        return jobAlertSuccess;
    };

    const jobAlertsProps: JobAlertsProps = {
        handleModalFormClose: () => {
            setLastClosedForm(new Date().toISOString());
            setShowFormModal(false);
            pushDataLayerEvent("jobAlertFormClosed");
        },
        handleSuccessModalClose: () => {
            pushDataLayerEvent("jobAlertSuccessClosed");
        },
        handleModalFormSubmit,
        defaultValues: {
            location: translateDMAString(userDMA, userCity)
        },
        showFormModal,
        newsletterOptin: userCountry === "United States",
        showBanner: false
    };

    return showJobAlert ? <JobAlerts {...jobAlertsProps} /> : null;
};

export { JobAlert, jobAlertSubscribeRequest };
